import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderCell,
  HeaderControlCell,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getSpoOrderForm,
  updateSpoOrderForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  DeleteIcon,
  DoctorIcon,
  FileCheckIcon,
  MedicalNotesIcon,
  PillIcon,
  PlusIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import { buildPhysicians } from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  DME_OPTIONS,
  DISCIPLINE_OPTIONS,
  EDUCATION_OPTIONS,
  EMR_CHECKLIST,
  LAB_ORDERS,
  MED_CHANGE_FLAGS,
  METHOD_RECEIVED_OPTIONS,
  OFFICE_DISCIPLINES,
  ORDER_CATEGORIES,
  PRIORITY_LEVELS,
  RESULTS_TO_OPTIONS,
  emptyMedicationRow,
  emptySpoPayload,
  toggleInList,
} from "./spoOrderContent";

const ORDER_DATE_MAX = new Date().toISOString().slice(0, 10);

const caregiverLabel = (form) => {
  const name = form?.filledByName || "";
  const filledRole = form?.filledByRole || "";
  if (name && filledRole) return `${name} (${filledRole})`;
  return name || filledRole || "—";
};
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptySpoPayload(),
  clinicianSignatureName: "",
  reviewComments: "",
});

const formatPatientDisplay = (patient) => {
  const name = [patient?.lastName, patient?.firstName].filter(Boolean).join(", ");
  if (!name) return patient?.mrn ? `(${patient.mrn})` : "";
  return patient?.mrn ? `${name} (${patient.mrn})` : name;
};

const DateFieldInput = ({
  name,
  value,
  canEdit,
  printMode,
  onChange,
  max,
}) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SpoOrderForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptySpoPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};
      const meds = Array.isArray(loadedPayload.medications)
        ? loadedPayload.medications
        : [];

      setValues({
        ...base,
        ...loadedPayload,
        orderDate:
          data.orderDate || loadedPayload.orderDate || today(),
        medications: meds.map((row) => ({
          id: row.id || emptyMedicationRow().id,
          medicationName: row.medicationName || "",
          dose: row.dose || "",
          route: row.route || "",
          frequency: row.frequency || "",
          startDate: row.startDate || "",
        })),
        orderCategories: Array.isArray(loadedPayload.orderCategories)
          ? loadedPayload.orderCategories
          : [],
        medicationChanges: Array.isArray(loadedPayload.medicationChanges)
          ? loadedPayload.medicationChanges
          : [],
        skilledDisciplines: Array.isArray(loadedPayload.skilledDisciplines)
          ? loadedPayload.skilledDisciplines
          : [],
        labOrders: Array.isArray(loadedPayload.labOrders)
          ? loadedPayload.labOrders
          : [],
        dmeEquipment: Array.isArray(loadedPayload.dmeEquipment)
          ? loadedPayload.dmeEquipment
          : [],
        educationProvided: Array.isArray(loadedPayload.educationProvided)
          ? loadedPayload.educationProvided
          : [],
        methodReceived: Array.isArray(loadedPayload.methodReceived)
          ? loadedPayload.methodReceived
          : [],
        disciplinesNotified: Array.isArray(loadedPayload.disciplinesNotified)
          ? loadedPayload.disciplinesNotified
          : [],
        emrChecklist: {
          ...base.emrChecklist,
          ...(loadedPayload.emrChecklist &&
          typeof loadedPayload.emrChecklist === "object"
            ? loadedPayload.emrChecklist
            : {}),
        },
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const physicians = buildPhysicians(patient);
    const primaryPhys = physicians[0];
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc =
      latestEpisode?.socDate || latestEpisode?.startDate || "";
    const certTo = latestEpisode?.endDate || latestEpisode?.toDate || "";

    if (isDraft) {
      hydrate({
        formName:
          clinicalForm.formName ||
          draft?.formName ||
          "Supplemental Physician Order",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        patientDateOfBirth: patient?.dateOfBirth || null,
        ...draftCaregiverIdentity(currentUser, role),
        createdAt: new Date().toISOString(),
        permissions: buildClinicalFormPermissions("pending", role),
        orderDate: today(),
        clinicianSignatureName: "",
        reviewComments: "",
        payload: {
          ...emptySpoPayload(),
          patientNameDisplay: formatPatientDisplay(patient),
          mrn: patient?.mrn || "",
          dateOfBirth: patient?.dateOfBirth
            ? formatDateForInput(patient.dateOfBirth)
            : "",
          socDate: soc ? formatDateForInput(soc) : "",
          certPeriodFrom: soc ? formatDateForInput(soc) : "",
          certPeriodTo: certTo ? formatDateForInput(certTo) : "",
          orderingPhysician: primaryPhys?.physicianName || "",
          physicianNpi: primaryPhys?.npiNumber || "",
          orderDate: today(),
        },
      });
      if (!cancelled) {
        setLoading(false);
        onReady?.();
      }
      return () => {
        cancelled = true;
      };
    }

    getSpoOrderForm(patientId, formId)
      .then((res) => {
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            patientNameDisplay:
              existingPayload.patientNameDisplay ||
              formatPatientDisplay(patient) ||
              loaded.patientName ||
              "",
            mrn: existingPayload.mrn || patient?.mrn || "",
            dateOfBirth:
              existingPayload.dateOfBirth ||
              (patient?.dateOfBirth
                ? formatDateForInput(patient.dateOfBirth)
                : "") ||
              loaded.patientDateOfBirth ||
              "",
            socDate:
              existingPayload.socDate ||
              (soc ? formatDateForInput(soc) : ""),
            certPeriodFrom:
              existingPayload.certPeriodFrom ||
              (soc ? formatDateForInput(soc) : ""),
            certPeriodTo:
              existingPayload.certPeriodTo ||
              (certTo ? formatDateForInput(certTo) : ""),
            orderingPhysician:
              existingPayload.orderingPhysician ||
              primaryPhys?.physicianName ||
              "",
            physicianNpi:
              existingPayload.physicianNpi || primaryPhys?.npiNumber || "",
          },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
    // Load once per form identity — unstable callbacks must not retrigger fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateMedicationRow = (rowId, patch) => {
    setValues((prev) => ({
      ...prev,
      medications: prev.medications.map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const removeMedicationRow = (rowId) => {
    setValues((prev) => ({
      ...prev,
      medications: prev.medications.filter((row) => row.id !== rowId),
    }));
  };

  const addMedicationRow = () => {
    setValues((prev) => ({
      ...prev,
      medications: [...prev.medications, emptyMedicationRow()],
    }));
  };

  const toggleEmrChecklist = (key) => {
    setValues((prev) => ({
      ...prev,
      emrChecklist: {
        ...prev.emrChecklist,
        [key]: !prev.emrChecklist?.[key],
      },
    }));
  };

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      reviewComments,
      ...spoFields
    } = values;

    const nestedPayload = {
      ...spoFields,
      orderDate: values.orderDate
        ? formatDateForInput(values.orderDate)
        : "",
      dateOfBirth: values.dateOfBirth
        ? formatDateForInput(values.dateOfBirth)
        : "",
      socDate: values.socDate ? formatDateForInput(values.socDate) : "",
      certPeriodFrom: values.certPeriodFrom
        ? formatDateForInput(values.certPeriodFrom)
        : "",
      certPeriodTo: values.certPeriodTo
        ? formatDateForInput(values.certPeriodTo)
        : "",
      effectiveDate: values.effectiveDate
        ? formatDateForInput(values.effectiveDate)
        : "",
      labCollectionDate: values.labCollectionDate
        ? formatDateForInput(values.labCollectionDate)
        : "",
      dateReceived: values.dateReceived
        ? formatDateForInput(values.dateReceived)
        : "",
      physicianAuthDate: values.physicianAuthDate
        ? formatDateForInput(values.physicianAuthDate)
        : "",
      processedDate: values.processedDate
        ? formatDateForInput(values.processedDate)
        : "",
      medications: (values.medications || []).map((row) => ({
        id: row.id,
        medicationName: row.medicationName || "",
        dose: row.dose || "",
        route: row.route || "",
        frequency: row.frequency || "",
        startDate: row.startDate ? formatDateForInput(row.startDate) : "",
      })),
      emrChecklist: {
        identityVerification: Boolean(values.emrChecklist?.identityVerification),
        timelyFiling: Boolean(values.emrChecklist?.timelyFiling),
        clinicalConsistency: Boolean(values.emrChecklist?.clinicalConsistency),
        documentationLinked: Boolean(values.emrChecklist?.documentationLinked),
      },
    };

    const payload = {
      flag,
      orderDate: values.orderDate
        ? formatDateForInput(values.orderDate)
        : null,
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    const sig = signatures.buildSignaturePayload();
    delete sig.patientSignatureImage;
    delete sig.clearPatientSignature;
    return { ...payload, ...sig };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateSpoOrderForm,
        filledByUserId,
        scheduleDate: values.orderDate
          ? formatDateForInput(values.orderDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showPhysicianSigPlaceholder =
    printMode || !values.physicianAuthName?.trim();

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Supplemental Physician Order (SPO)
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <section className="rounded-md border border-slate-800 overflow-hidden">
          <div className="grid grid-cols-5 gap-x-2.5 items-start px-3 py-1.5">
            <HeaderCell
              label="Patient Name"
              value={patientName || form.patientName}
              dense
            />
            <HeaderCell label="MR Number" value={patient?.mrn || values.mrn} dense />
            <HeaderCell
              label="Date of Birth"
              value={displayDateUS(
                patient?.dateOfBirth || values.dateOfBirth || form.patientDateOfBirth,
              )}
              dense
            />
            {caregiverSlot || (
              <HeaderCell
                label="Caregiver Name"
                value={caregiverLabel(form)}
                dense
              />
            )}
            {printMode ? (
              <HeaderCell
                label="Date"
                value={displayDateUS(values.orderDate)}
                dense
              />
            ) : (
              <HeaderControlCell label="Date">
                <InputDatePicker
                  name="orderDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.orderDate) || ""}
                  onChange={(event) =>
                    setValue("orderDate", event.target.value)
                  }
                  max={ORDER_DATE_MAX}
                  className="min-w-0 w-full"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </HeaderControlCell>
            )}
          </div>
        </section>

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <Section icon={CalendarIcon} title="1. Patient Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Patient Name">
              <input
                type="text"
                className={fieldClass}
                value={values.patientNameDisplay}
                disabled={!canEdit}
                onChange={(e) => setValue("patientNameDisplay", e.target.value)}
              />
            </Field>
            <Field label="MRN">
              <input
                type="text"
                className={fieldClass}
                value={values.mrn}
                disabled={!canEdit}
                onChange={(e) => setValue("mrn", e.target.value)}
              />
            </Field>
            <Field label="Date of Birth">
              <DateFieldInput
                name="dateOfBirth"
                value={values.dateOfBirth}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("dateOfBirth", v)}
              />
            </Field>
            <Field label="SOC Date">
              <DateFieldInput
                name="socDate"
                value={values.socDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("socDate", v)}
              />
            </Field>
            <Field label="Cert From">
              <DateFieldInput
                name="certPeriodFrom"
                value={values.certPeriodFrom}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("certPeriodFrom", v)}
              />
            </Field>
            <Field label="Cert To">
              <DateFieldInput
                name="certPeriodTo"
                value={values.certPeriodTo}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("certPeriodTo", v)}
              />
            </Field>
            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Primary Diagnoses">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.primaryDiagnoses}
                  disabled={!canEdit}
                  placeholder="ICD codes and diagnosis narrative…"
                  onChange={(e) => setValue("primaryDiagnoses", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Ordering Physician">
              <input
                type="text"
                className={fieldClass}
                value={values.orderingPhysician}
                disabled={!canEdit}
                onChange={(e) => setValue("orderingPhysician", e.target.value)}
              />
            </Field>
            <Field label="NPI">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianNpi}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianNpi", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="2. Supplemental Order Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Order Number">
              <input
                type="text"
                className={fieldClass}
                value={values.orderNumber}
                disabled={!canEdit}
                onChange={(e) => setValue("orderNumber", e.target.value)}
              />
            </Field>
            <Field label="Order Date">
              <DateFieldInput
                name="orderDate"
                value={values.orderDate}
                canEdit={canEdit}
                printMode={printMode}
                max={ORDER_DATE_MAX}
                onChange={(v) => setValue("orderDate", v)}
              />
            </Field>
            <Field label="Effective Date">
              <DateFieldInput
                name="effectiveDate"
                value={values.effectiveDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("effectiveDate", v)}
              />
            </Field>
          </div>

          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Priority Level
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRIORITY_LEVELS.map((level) => {
                const active = values.priorityLevel === level;
                return (
                  <button
                    key={level}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setValue("priorityLevel", level)}
                    className={`h-7 px-2.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-60 ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
                    }`}
                  >
                    {level}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Order Category
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {ORDER_CATEGORIES.map((cat) => (
                <label
                  key={cat}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={values.orderCategories.includes(cat)}
                    disabled={!canEdit}
                    onChange={() => toggleListValue("orderCategories", cat)}
                  />
                  <span>{cat}</span>
                </label>
              ))}
            </div>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="3. Physician Order (Required)">
          <Field label="Order Details / Clinical Justification">
            <textarea
              rows={6}
              className={textareaClass}
              value={values.orderDetailsJustification}
              disabled={!canEdit}
              placeholder="Enter complete and specific physician orders with clinical justification…"
              onChange={(e) =>
                setValue("orderDetailsJustification", e.target.value)
              }
            />
          </Field>
          <p className="mt-1.5 text-[11px] italic text-slate-500">
            Clinician note: Orders must be complete, specific, and clinically
            justified. Incomplete orders may be returned for clarification.
          </p>
        </Section>

        <Section icon={PillIcon} title="4. Medication Orders">
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr
                  className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-2 py-1.5 font-semibold">Medication Name</th>
                  <th className="px-2 py-1.5 font-semibold">Dose</th>
                  <th className="px-2 py-1.5 font-semibold">Route</th>
                  <th className="px-2 py-1.5 font-semibold">Freq</th>
                  <th className="px-2 py-1.5 font-semibold w-32">Start Date</th>
                  {canEdit && (
                    <th className="px-2 py-1.5 font-semibold w-12 text-right">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {values.medications.length === 0 ? (
                  <tr className="border-t border-slate-100">
                    <td
                      colSpan={canEdit ? 6 : 5}
                      className="px-2 py-3 text-[11px] text-slate-400 text-center"
                    >
                      No medications added
                    </td>
                  </tr>
                ) : (
                  values.medications.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.medicationName}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateMedicationRow(row.id, {
                              medicationName: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.dose}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateMedicationRow(row.id, { dose: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.route}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateMedicationRow(row.id, {
                              route: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.frequency}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateMedicationRow(row.id, {
                              frequency: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <DateFieldInput
                          name={`med-start-${row.id}`}
                          value={row.startDate}
                          canEdit={canEdit}
                          printMode={printMode}
                          onChange={(v) =>
                            updateMedicationRow(row.id, { startDate: v })
                          }
                        />
                      </td>
                      {canEdit && (
                        <td className="px-2 py-1 text-right">
                          <button
                            type="button"
                            onClick={() => removeMedicationRow(row.id)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                            aria-label="Remove medication"
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={addMedicationRow}
                className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
              >
                <PlusIcon size={13} />
                Add Medication
              </button>
            </div>
          )}

          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Medication Changes
            </div>
            <div className="flex flex-wrap gap-3">
              {MED_CHANGE_FLAGS.map((flag) => (
                <label
                  key={flag}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={values.medicationChanges.includes(flag)}
                    disabled={!canEdit}
                    onChange={() => toggleListValue("medicationChanges", flag)}
                  />
                  <span>{flag}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <Field label="Comments">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.medicationComments}
                disabled={!canEdit}
                onChange={(e) => setValue("medicationComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={DoctorIcon} title="5. Skilled Service Orders">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Disciplines
              </div>
              <div className="space-y-1.5">
                {DISCIPLINE_OPTIONS.map((d) => (
                  <label
                    key={d.key}
                    className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.skilledDisciplines.includes(d.key)}
                      disabled={!canEdit}
                      onChange={() =>
                        toggleListValue("skilledDisciplines", d.key)
                      }
                    />
                    <span>
                      <span className="font-semibold">{d.label}</span>
                      {d.hint ? (
                        <span className="text-slate-500"> — {d.hint}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Field label="Frequency">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.skilledFrequency}
                  disabled={!canEdit}
                  placeholder="e.g. 2w1 × 4 weeks"
                  onChange={(e) => setValue("skilledFrequency", e.target.value)}
                />
              </Field>
              <Field label="Duration">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.skilledDuration}
                  disabled={!canEdit}
                  onChange={(e) => setValue("skilledDuration", e.target.value)}
                />
              </Field>
              <Field label="Special Instructions">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.skilledSpecialInstructions}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("skilledSpecialInstructions", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="6. Wound Care Orders">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Location">
              <input
                type="text"
                className={fieldClass}
                value={values.woundLocation}
                disabled={!canEdit}
                onChange={(e) => setValue("woundLocation", e.target.value)}
              />
            </Field>
            <Field label="Treatment">
              <input
                type="text"
                className={fieldClass}
                value={values.woundTreatment}
                disabled={!canEdit}
                onChange={(e) => setValue("woundTreatment", e.target.value)}
              />
            </Field>
            <Field label="Dressing Type">
              <input
                type="text"
                className={fieldClass}
                value={values.woundDressingType}
                disabled={!canEdit}
                onChange={(e) => setValue("woundDressingType", e.target.value)}
              />
            </Field>
            <Field label="Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.woundFrequency}
                disabled={!canEdit}
                onChange={(e) => setValue("woundFrequency", e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Special Instructions">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.woundSpecialInstructions}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("woundSpecialInstructions", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="7. Laboratory / Diagnostic">
          <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Lab Orders
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
            {LAB_ORDERS.map((lab) => (
              <label
                key={lab}
                className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.labOrders.includes(lab)}
                  disabled={!canEdit}
                  onChange={() => toggleListValue("labOrders", lab)}
                />
                <span>{lab}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Collection Date">
              <DateFieldInput
                name="labCollectionDate"
                value={values.labCollectionDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("labCollectionDate", v)}
              />
            </Field>
            <Field label="Results To">
              <select
                className={fieldClass}
                value={values.labResultsTo}
                disabled={!canEdit}
                onChange={(e) => setValue("labResultsTo", e.target.value)}
              >
                {RESULTS_TO_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="8. DME / Equipment">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
            {DME_OPTIONS.map((item) => (
              <label
                key={item}
                className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.dmeEquipment.includes(item)}
                  disabled={!canEdit}
                  onChange={() => toggleListValue("dmeEquipment", item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
          <Field label="Instructions">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.dmeInstructions}
              disabled={!canEdit}
              onChange={(e) => setValue("dmeInstructions", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={ShieldCheckIcon} title="9. Patient / Caregiver Notification">
          <div className="mb-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Patient / Caregiver Informed
            </div>
            <div className="flex flex-wrap gap-4">
              {["Yes", "No"].map((opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="patientInformed"
                    className={checkboxClass}
                    checked={values.patientInformed === opt}
                    disabled={!canEdit}
                    onChange={() => setValue("patientInformed", opt)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Education Provided
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {EDUCATION_OPTIONS.map((item) => (
                <label
                  key={item}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={values.educationProvided.includes(item)}
                    disabled={!canEdit}
                    onChange={() => toggleListValue("educationProvided", item)}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>

          <Field label="Comments">
            <textarea
              rows={2}
              className={textareaClass}
              value={values.notificationComments}
              disabled={!canEdit}
              onChange={(e) => setValue("notificationComments", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={ShieldCheckIcon} title="10. Agency Verification">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
            <Field label="Received By">
              <input
                type="text"
                className={fieldClass}
                value={values.receivedByClinician}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("receivedByClinician", e.target.value)
                }
              />
            </Field>
            <Field label="Title">
              <input
                type="text"
                className={fieldClass}
                value={values.receivedByTitle}
                disabled={!canEdit}
                onChange={(e) => setValue("receivedByTitle", e.target.value)}
              />
            </Field>
            <Field label="Date Received">
              <DateFieldInput
                name="dateReceived"
                value={values.dateReceived}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("dateReceived", v)}
              />
            </Field>
            <Field label="Time">
              <input
                type="time"
                className={fieldClass}
                value={values.timeReceived}
                disabled={!canEdit}
                onChange={(e) => setValue("timeReceived", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Method Received
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {METHOD_RECEIVED_OPTIONS.map((method) => (
                <label
                  key={method}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={values.methodReceived.includes(method)}
                    disabled={!canEdit}
                    onChange={() => toggleListValue("methodReceived", method)}
                  />
                  <span>{method}</span>
                </label>
              ))}
            </div>
          </div>
        </Section>

        <Section icon={DoctorIcon} title="11. Physician Authentication">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
            {showPhysicianSigPlaceholder && (
              <div className="sm:col-span-2 lg:col-span-4 rounded-md border border-dashed border-slate-300 bg-slate-50 min-h-[72px] flex items-center justify-center px-3 text-[11px] text-slate-400 italic text-center">
                Place Physician Electronic Signature Here
              </div>
            )}
            <Field label="Physician Name" span="sm:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianAuthName}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianAuthName", e.target.value)}
              />
            </Field>
            <Field label="Date">
              <DateFieldInput
                name="physicianAuthDate"
                value={values.physicianAuthDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("physicianAuthDate", v)}
              />
            </Field>
            <Field label="Time">
              <input
                type="time"
                className={fieldClass}
                value={values.physicianAuthTime}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianAuthTime", e.target.value)}
              />
            </Field>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
            Confidential patient health information. Unauthorized disclosure is
            prohibited under applicable privacy laws.
          </p>
        </Section>

        <Section
          icon={FileCheckIcon}
          title="12. Agency Processing (Office Use Only)"
          className="bg-slate-50/80"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Processed By">
              <input
                type="text"
                className={fieldClass}
                value={values.processedBy}
                disabled={!canEdit}
                onChange={(e) => setValue("processedBy", e.target.value)}
              />
            </Field>
            <Field label="Date">
              <DateFieldInput
                name="processedDate"
                value={values.processedDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("processedDate", v)}
              />
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Entered Into EMR
              </div>
              <div className="flex flex-wrap gap-4">
                {["Yes", "No"].map((opt) => (
                  <label
                    key={`emr-${opt}`}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="enteredIntoEmr"
                      className={checkboxClass}
                      checked={values.enteredIntoEmr === opt}
                      disabled={!canEdit}
                      onChange={() => setValue("enteredIntoEmr", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                QA Reviewed
              </div>
              <div className="flex flex-wrap gap-4">
                {["Yes", "No"].map((opt) => (
                  <label
                    key={`qa-${opt}`}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="qaReviewed"
                      className={checkboxClass}
                      checked={values.qaReviewed === opt}
                      disabled={!canEdit}
                      onChange={() => setValue("qaReviewed", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Disciplines Notified
            </div>
            <div className="flex flex-wrap gap-3">
              {OFFICE_DISCIPLINES.map((disc) => (
                <label
                  key={disc}
                  className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={values.disciplinesNotified.includes(disc)}
                    disabled={!canEdit}
                    onChange={() =>
                      toggleListValue("disciplinesNotified", disc)
                    }
                  />
                  <span>{disc}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              EMR Checklist
            </div>
            <div className="space-y-2">
              {EMR_CHECKLIST.map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer rounded-md border border-slate-200 bg-white px-2.5 py-2"
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={Boolean(values.emrChecklist?.[item.key])}
                    disabled={!canEdit}
                    onChange={() => toggleEmrChecklist(item.key)}
                  />
                  <span className="min-w-0">
                    <span className="font-semibold block">{item.title}</span>
                    <span className="text-[11px] text-slate-500">
                      {item.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

SpoOrderForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default SpoOrderForm;
